import React from "react";
import { ArrowRightLeft, BadgeCheck, CheckCircle2, ClipboardCheck, FileSignature, Gavel, Pencil, Route, TimerOff, Undo2, UserCheck, XCircle } from "lucide-react";
import { Badge } from "../ui/badge";
import { fmtDateTime } from "../../lib/api";

/** Phase 207 — SAF status badge + action timeline (shared by citizen, collector, supervisor, admin screens). */
export const SAF_STATUS = {
  pending_verification: { label: "Pending Physical Verification", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  collector_verified: { label: "Pending Supervisor Review", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  sent_back: { label: "Sent Back for Correction", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  supervisor_approved: { label: "Pending Admin Approval", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  approved: { label: "Assessment Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
};
export const safStatusLabel = (s) => SAF_STATUS[s]?.label || (s || "").replace(/_/g, " ");

export const SafStatusBadge = ({ status, className = "", testId }) => {
  const m = SAF_STATUS[status] || { label: safStatusLabel(status), cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <Badge variant="outline" className={`${m.cls} font-medium whitespace-nowrap ${className}`} data-testid={testId || `saf-status-${status}`}>{m.label}</Badge>;
};

/**
 * Phase 210 — routing / escalation / direct-decision chips. `tags` is `saf.escalation_tags` from the API:
 * [{key, label, tone: escalation | routing | direct}].
 */
const TAG_TONE = {
  escalation: { cls: "bg-rose-50 text-rose-700 border-rose-200", Icon: TimerOff },
  routing: { cls: "bg-slate-100 text-slate-700 border-slate-300", Icon: Route },
  direct: { cls: "bg-indigo-50 text-indigo-700 border-indigo-200", Icon: Gavel },
};
export const SafEscalationTags = ({ tags = [], className = "", testId = "saf-tags", size = "xs" }) => {
  if (!tags || tags.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${className}`} data-testid={testId}>
      {tags.map((t) => {
        const tone = TAG_TONE[t.tone] || TAG_TONE.routing;
        const Icon = tone.Icon;
        return (
          <Badge key={t.key} variant="outline" className={`${tone.cls} gap-1 whitespace-nowrap font-medium ${size === "xs" ? "text-[10px] px-1.5 py-0" : ""}`} data-testid={`${testId}-${t.key}`}>
            <Icon className="h-3 w-3" aria-hidden="true" /> {t.label}
          </Badge>
        );
      })}
    </div>
  );
};

/** Human copy for the stage clock: "escalates to the Supervisor in 3 h 20 m" / "overdue — escalates on the next check". */
export const stageDeadlineText = (saf) => {
  if (!saf?.stage_deadline_at || !["collector", "supervisor"].includes(saf.stage)) return null;
  const to = saf.stage === "collector" ? "Supervisor" : "Municipal Admin";
  const ms = new Date(saf.stage_deadline_at).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return { overdue: true, to, text: `Time limit (${saf.stage_hours} h) exceeded — auto-escalates to the ${to} on the next check` };
  const h = Math.floor(ms / 3600000); const m = Math.floor((ms % 3600000) / 60000);
  return { overdue: false, to, text: `Auto-escalates to the ${to} in ${h > 0 ? `${h} h ` : ""}${m} m if no action (limit ${saf.stage_hours} h)` };
};

const ACTION_ICON = {
  submitted: FileSignature, assigned: ArrowRightLeft, routed: Route, collector_verified: UserCheck, demand_modified: Pencil,
  supervisor_approved: ClipboardCheck, sent_back: Undo2, auto_escalated: TimerOff, final_approved: BadgeCheck, direct_approved: Gavel,
  rejected: XCircle, direct_rejected: Gavel,
};
const ACTION_CLS = {
  final_approved: "text-emerald-700 bg-emerald-50 border-emerald-200", supervisor_approved: "text-emerald-700 bg-emerald-50 border-emerald-200",
  direct_approved: "text-indigo-700 bg-indigo-50 border-indigo-200", direct_rejected: "text-indigo-700 bg-indigo-50 border-indigo-200",
  rejected: "text-red-700 bg-red-50 border-red-200", sent_back: "text-orange-700 bg-orange-50 border-orange-200",
  demand_modified: "text-amber-700 bg-amber-50 border-amber-200", auto_escalated: "text-rose-700 bg-rose-50 border-rose-200",
  routed: "text-slate-700 bg-slate-100 border-slate-300",
};

export const SafActionBadge = ({ action, label, testId }) => {
  const Icon = ACTION_ICON[action] || CheckCircle2;
  return (
    <Badge variant="outline" className={`gap-1 whitespace-nowrap ${ACTION_CLS[action] || "text-primary bg-primary/10 border-primary/20"}`} data-testid={testId}>
      <Icon className="h-3 w-3" aria-hidden="true" /> {label || action}
    </Badge>
  );
};

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ValuesPreview = ({ values, title, testId }) => {
  if (!values) return null;
  if (values.demand_lines) {
    return (
      <div className="text-[11px] rounded-md border bg-muted/40 p-2" data-testid={testId}>
        <p className="font-medium mb-1">{title}{values.source ? <span className="text-muted-foreground font-normal"> ({values.source})</span> : null}</p>
        {values.demand_lines.map((ln, i) => (
          <p key={i} className="flex justify-between gap-2"><span className="truncate">{ln.tax_head}{ln.usage_label ? ` (${ln.usage_label})` : ""}{ln.fy ? ` · ${ln.fy}` : ""}</span>
            <span className="tabular-nums whitespace-nowrap">A {fmtMoney(ln.arrear)} / C {fmtMoney(ln.current)}</span></p>
        ))}
        <p className="flex justify-between font-semibold border-t mt-1 pt-1"><span>Total</span><span className="tabular-nums">{fmtMoney(values.total)}</span></p>
      </div>
    );
  }
  const entries = Object.entries(values).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (entries.length === 0) return null;
  return (
    <div className="text-[11px] rounded-md border bg-muted/40 p-2" data-testid={testId}>
      <p className="font-medium mb-1">{title}</p>
      {entries.map(([k, v]) => <p key={k}><span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span> {typeof v === "object" ? JSON.stringify(v) : String(v)}</p>)}
    </div>
  );
};

export function SafTimeline({ history = [], testId = "saf-timeline", compact = false }) {
  if (!history.length) return <p className="text-xs text-muted-foreground" data-testid={`${testId}-empty`}>No actions recorded yet.</p>;
  return (
    <ol className="relative border-l border-border ml-2 space-y-3" data-testid={testId}>
      {[...history].reverse().map((h) => (
        <li key={h.id} className="ml-4" data-testid={`${testId}-item-${h.action}`}>
          <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-primary/70 ring-4 ring-background" aria-hidden="true" />
          <div className="flex items-center gap-2 flex-wrap">
            <SafActionBadge action={h.action} label={h.action_label} />
            <span className="text-xs text-muted-foreground tabular-nums">{fmtDateTime(h.at)}</span>
          </div>
          <p className="text-xs mt-1">
            <span className="font-medium">{h.by_label || h.action_by_name}</span>
            {h.action_by_role_label ? <span className="text-muted-foreground"> · {h.action_by_role_label}</span> : null}
          </p>
          {h.remarks && <p className="text-xs text-foreground/90 mt-0.5 whitespace-pre-wrap break-words" data-testid={`${testId}-remarks-${h.action}`}>“{h.remarks}”</p>}
          {!compact && (h.old_values || h.new_values) && (h.action === "demand_modified" || h.action === "collector_verified" || h.action === "final_approved" || h.action === "direct_approved") && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1.5">
              <ValuesPreview values={h.old_values} title="Old values" testId={`${testId}-old-${h.id}`} />
              <ValuesPreview values={h.new_values} title={(h.action === "final_approved" || h.action === "direct_approved") ? `Approved${h.new_values?.pin ? ` · PIN ${h.new_values.pin}` : ""}` : "New values"} testId={`${testId}-new-${h.id}`} />
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
